import { useMemo } from 'react';
import { useT } from '@/i18n/LanguageContext';
import { visualizeContent } from '@/i18n/content/visualize';
import { Trigger } from '@/hooks/useTriggerStream';
import { buildDeviceColorMap, hashTo } from './vizPalette';

interface Props {
  triggers: Trigger[];
  dark: boolean;
  bare?: boolean;
}

/**
 * Pond, seen from above. Each device is a drop point; every PIR trigger widens
 * its ripple, so busy sensors spread wide rings across the water while quiet
 * ones stay small dimples.
 */
export function PondRipples({ triggers, dark, bare = false }: Props) {
  const t = useT(visualizeContent).charts;
  const W = 1200;
  const H = 760;
  const CX = W / 2;
  const CY = H / 2;

  const { drops, maxCount, total } = useMemo(() => {
    const counts = new Map<string, number>();
    triggers.forEach((t) => {
      if (!t.device_id) return;
      counts.set(t.device_id, (counts.get(t.device_id) || 0) + 1);
    });

    const ids = Array.from(counts.keys()).sort();
    const colors = buildDeviceColorMap(ids);
    const maxCount = Math.max(1, ...ids.map((id) => counts.get(id)!));

    // Phyllotaxis placement: deterministic, evenly spread, no overlap clusters.
    const GOLDEN = Math.PI * (3 - Math.sqrt(5));
    const spread = Math.min(W, H) * 0.45;
    const n = Math.max(1, ids.length);

    const drops = ids.map((id, idx) => {
      const t = n === 1 ? 0 : idx / (n - 1);
      const radius = spread * Math.sqrt((idx + 0.5) / n);
      const angle = idx * GOLDEN + (hashTo(id, 5) % 100) / 100;
      const count = counts.get(id)!;
      const maxR = 22 + Math.sqrt(count / maxCount) * 130;
      const ringCount = Math.max(2, Math.round(2 + (count / maxCount) * 7));
      return {
        id,
        count,
        color: colors.get(id) || 'var(--viz-accent)',
        x: CX + Math.cos(angle) * radius,
        y: CY + Math.sin(angle) * radius * 0.62, // slight top-view foreshortening
        maxR,
        ringCount,
        phase: (hashTo(id, 31) % 1000) / 1000,
        t,
      };
    });

    return { drops, maxCount, total: triggers.length };
  }, [triggers]);

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" style={{ maxHeight: '85vh' }}>
        <defs>
          <radialGradient id="pondWater">
            <stop offset="0%" stopColor="var(--viz-accent)" stopOpacity="0.05" />
            <stop offset="100%" stopColor="var(--viz-accent)" stopOpacity="0.01" />
          </radialGradient>
          {drops.map((d) => (
            <radialGradient key={`g-${d.id}`} id={`drop-${d.id.replace(/[^a-zA-Z0-9]/g, '')}`}>
              <stop offset="0%" stopColor={d.color} stopOpacity="0.35" />
              <stop offset="70%" stopColor={d.color} stopOpacity="0.06" />
              <stop offset="100%" stopColor={d.color} stopOpacity="0" />
            </radialGradient>
          ))}
        </defs>

        {/* Water surface */}
        <rect x={0} y={0} width={W} height={H} fill="url(#pondWater)" />

        {drops.map((d) => {
          const gid = `drop-${d.id.replace(/[^a-zA-Z0-9]/g, '')}`;
          return (
            <g key={d.id}>
              <circle cx={d.x} cy={d.y} r={d.maxR} fill={`url(#${gid})`} />
              {Array.from({ length: d.ringCount }, (_, i) => {
                const f = (i + 1) / d.ringCount;
                return (
                  <circle
                    key={i}
                    cx={d.x}
                    cy={d.y}
                    r={d.maxR * f}
                    fill="none"
                    stroke={d.color}
                    strokeWidth={Math.max(0.5, 1.8 * (1 - f) + 0.4)}
                    opacity={0.55 * (1 - f * 0.75)}
                  >
                    <animate
                      attributeName="r"
                      values={`${(d.maxR * f * 0.25).toFixed(1)};${d.maxR.toFixed(1)}`}
                      dur={`${4 + d.phase * 3}s`}
                      begin={`${(i / d.ringCount) * (4 + d.phase * 3)}s`}
                      repeatCount="indefinite"
                    />
                    <animate
                      attributeName="opacity"
                      values="0.6;0"
                      dur={`${4 + d.phase * 3}s`}
                      begin={`${(i / d.ringCount) * (4 + d.phase * 3)}s`}
                      repeatCount="indefinite"
                    />
                  </circle>
                );
              })}
              {/* Impact point */}
              <circle cx={d.x} cy={d.y} r={2 + (d.count / maxCount) * 4} fill={d.color} opacity={0.9} />
            </g>
          );
        })}
      </svg>
      {!bare && (
        <div className="mt-3 text-xs font-mono tracking-wide text-sitefg/40">
          {t.pondCaption(total.toLocaleString(), maxCount.toLocaleString())}
        </div>
      )}
    </div>
  );
}
